import { useEffect, useState, useRef } from "react";
import { motion, AnimatePresence, useInView, animate, useScroll, useTransform } from "framer-motion";
import { ArrowRight, Coffee, MousePointerClick } from "lucide-react";
import api, { imgUrl, track } from "@/lib/api";
import Navbar from "@/components/public/Navbar";
import Footer from "@/components/public/Footer";
import LeadForm from "@/components/public/LeadForm";
import BackgroundVideo from "@/components/public/BackgroundVideo";

const ROTATING = ["vos brochures", "vos flyers", "vos livres", "vos logos", "vos impressions"];

export default function Home() {
  const [settings, setSettings] = useState(null);
  const [categories, setCategories] = useState([]);
  const [portfolio, setPortfolio] = useState([]);
  const [logos, setLogos] = useState([]);
  const [active, setActive] = useState("all");
  const [lightbox, setLightbox] = useState(null);
  const [wordIdx, setWordIdx] = useState(0);

  useEffect(() => {
    track("page_view", "/");
    api.get("/settings").then((r) => setSettings(r.data));
    api.get("/categories").then((r) => setCategories(r.data));
    api.get("/portfolio").then((r) => setPortfolio(r.data));
    api.get("/logos").then((r) => setLogos(r.data));
  }, []);

  useEffect(() => {
    const t = setInterval(() => setWordIdx((i) => (i + 1) % ROTATING.length), 2200);
    return () => clearInterval(t);
  }, []);

  useEffect(() => {
    if (window.location.hash) {
      setTimeout(() => document.getElementById(window.location.hash.slice(1))?.scrollIntoView({ behavior: "smooth" }), 400);
    }
  }, [settings]);

  const filtered = active === "all" ? portfolio : portfolio.filter((p) => p.category === active);

  const heroRef = useRef(null);
  const { scrollYProgress } = useScroll({ target: heroRef, offset: ["start start", "end start"] });
  const videoY = useTransform(scrollYProgress, [0, 1], ["0%", "22%"]);
  const contentY = useTransform(scrollYProgress, [0, 1], ["0%", "35%"]);
  const contentOpacity = useTransform(scrollYProgress, [0, 0.85], [1, 0]);

  const [isMobile, setIsMobile] = useState(false);
  useEffect(() => {
    const mq = window.matchMedia("(max-width: 1023px)");
    const update = () => setIsMobile(mq.matches);
    update();
    mq.addEventListener("change", update);
    return () => mq.removeEventListener("change", update);
  }, []);

  // Disable the scroll-linked parallax/fade on mobile & tablet so the form stays usable
  const heroContentStyle = isMobile ? undefined : { y: contentY, opacity: contentOpacity };
  const heroVideoStyle = isMobile ? undefined : { y: videoY };

  return (
    <div className="relative min-h-screen bg-[#09090B] text-white overflow-x-hidden">
      <a href="#realisations" className="skip-link">Aller au contenu</a>
      <div className="grain" />
      <Navbar settings={settings} />

      <main id="main-content">
      {/* HERO */}
      <section ref={heroRef} className="relative pt-32 pb-20 lg:pt-40 lg:pb-28 min-h-[90vh] flex items-center">
        <motion.div style={heroVideoStyle} className="absolute inset-0">
          <BackgroundVideo />
        </motion.div>
        <motion.div style={heroVideoStyle} className="absolute top-20 -right-40 w-[500px] h-[500px] bg-yellow-400/10 rounded-full blur-[120px] z-[2]" />
        <motion.div style={heroContentStyle} className="max-w-7xl mx-auto px-6 lg:px-8 grid lg:grid-cols-2 gap-16 items-center relative z-10 w-full">
          <div>
            <p className="text-sm uppercase tracking-[0.3em] text-yellow-400 font-semibold mb-6">
              Graphiste Freelance
            </p>
            <h1 className="font-display text-5xl sm:text-6xl lg:text-7xl font-black tracking-tighter leading-[0.95]">
              Je réalise
              <br />
              <span className="inline-block h-[1.1em] overflow-hidden align-top">
                <AnimatePresence mode="wait">
                  <motion.span
                    key={wordIdx}
                    initial={{ y: "100%", opacity: 0 }}
                    animate={{ y: 0, opacity: 1 }}
                    exit={{ y: "-100%", opacity: 0 }}
                    transition={{ duration: 0.4 }}
                    className="inline-block text-yellow-400"
                  >
                    {ROTATING[wordIdx]}
                  </motion.span>
                </AnimatePresence>
              </span>
            </h1>
            <p className="mt-8 text-zinc-400 text-lg max-w-md leading-relaxed">
              {settings?.tagline || "Design graphique, identité visuelle & impression."} Plus de 20 ans d'expérience au service de vos projets.
            </p>
            <div className="mt-8 flex gap-4">
              <button onClick={() => document.getElementById("realisations")?.scrollIntoView({ behavior: "smooth" })}
                className="group flex items-center gap-2 bg-yellow-400 text-black font-bold px-6 py-3.5 rounded-md hover:bg-yellow-300 transition-colors">
                Voir mes réalisations <ArrowRight className="w-4 h-4 group-hover:translate-x-1 transition-transform" />
              </button>
            </div>
          </div>

          {/* Callback form card */}
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6, delay: 0.2 }}
            className="bg-zinc-900/60 backdrop-blur-xl border border-white/10 rounded-2xl p-8"
          >
            <h2 className="font-display text-2xl font-bold mb-1">Demande de rappel</h2>
            <p className="text-zinc-400 text-sm mb-6">Laissez vos coordonnées, je vous recontacte.</p>
            <LeadForm compact />
          </motion.div>
        </motion.div>
      </section>

      {/* ABOUT + SKILLS */}
      <section id="apropos" className="relative py-24 border-t border-white/5">
        <div className="max-w-7xl mx-auto px-6 lg:px-8 grid lg:grid-cols-2 gap-16 items-center">
          <div>
            {settings?.avatar_url && (
              <img src={imgUrl(settings.avatar_url)} alt="Julien Chaudeaux" className="w-40 mb-8" />
            )}
            <h2 className="font-display text-4xl tracking-tight mb-4">Julien Chaudeaux</h2>

            {/* Métiers mis en évidence */}
            <div className="flex flex-wrap gap-2 mb-6">
              {["Dessinateur", "Graphiste", "Designer Freelance", "Web designer"].map((m, i) => (
                <motion.span
                  key={m}
                  initial={{ opacity: 0, y: 10 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  viewport={{ once: true }}
                  transition={{ delay: i * 0.1 }}
                  className="px-4 py-1.5 rounded-full text-sm font-semibold bg-yellow-400/10 text-yellow-400 border border-yellow-400/30"
                >
                  {m}
                </motion.span>
              ))}
            </div>

            <p className="text-zinc-400 leading-relaxed">{settings?.about}</p>

            {/* Highlights */}
            <div className="grid grid-cols-2 gap-4 mt-8">
              <div className="rounded-xl border border-white/10 bg-zinc-900/40 p-5">
                <div className="font-display text-4xl text-yellow-400 leading-none">
                  <CountUp to={20} suffix="+" />
                </div>
                <div className="text-sm text-zinc-300 mt-2 font-medium">ans d'expérience</div>
              </div>
              <div className="rounded-xl border border-white/10 bg-zinc-900/40 p-5">
                <div className="font-display text-2xl text-white leading-tight">Gobelins</div>
                <div className="text-sm text-zinc-400 mt-2 font-medium">Diplômé de l'École</div>
              </div>
            </div>

            <div className="flex gap-8 mt-6">
              <div className="flex items-center gap-3">
                <Coffee className="text-yellow-400" />
                <div><div className="font-display text-2xl">5</div><div className="text-xs text-zinc-500">Cafés / jour</div></div>
              </div>
              <div className="flex items-center gap-3">
                <MousePointerClick className="text-yellow-400" />
                <div><div className="font-display text-2xl">3000</div><div className="text-xs text-zinc-500">Clics / jour</div></div>
              </div>
            </div>
          </div>
          <div className="grid grid-cols-2 gap-5">
            {(settings?.skills || []).map((sk, i) => (
              <RadialSkill key={sk.name} sk={sk} i={i} />
            ))}
          </div>
        </div>
      </section>

      {/* PORTFOLIO */}
      <section id="realisations" className="relative py-24 border-t border-white/5">
        <div className="max-w-7xl mx-auto px-6 lg:px-8">
          <p className="text-sm uppercase tracking-[0.3em] text-yellow-400 font-semibold mb-3">Portfolio</p>
          <h2 className="font-display text-4xl sm:text-5xl font-black tracking-tighter mb-10">Quelques-unes de mes réalisations</h2>

          <div className="flex flex-wrap gap-3 mb-10">
            <FilterBtn label="Tout" isActive={active === "all"} onClick={() => setActive("all")} testid="filter-all" />
            {categories.map((c) => (
              <FilterBtn key={c.slug} label={c.name} isActive={active === c.slug} onClick={() => setActive(c.slug)} testid={`filter-${c.slug}`} />
            ))}
          </div>

          <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-4">
            {filtered.map((item, i) => (
              <motion.button
                key={item.id}
                layout
                data-testid="portfolio-item"
                onClick={() => setLightbox(item)}
                initial={{ opacity: 0, scale: 0.95 }}
                whileInView={{ opacity: 1, scale: 1 }}
                viewport={{ once: true }}
                transition={{ duration: 0.3, delay: (i % 8) * 0.03 }}
                className="group relative aspect-[3/4] overflow-hidden rounded-lg bg-zinc-900 border border-white/5"
              >
                <img src={imgUrl(item.image_url)} alt={item.alt_text} loading="lazy" className="w-full h-full object-cover transition-transform duration-500 group-hover:scale-105" />
                <div className="absolute inset-0 bg-black/70 opacity-0 group-hover:opacity-100 transition-opacity flex items-end p-4">
                  <span className="text-yellow-400 font-medium text-sm">{item.title}</span>
                </div>
              </motion.button>
            ))}
          </div>
          {filtered.length === 0 && <p className="text-zinc-500 py-12 text-center">Aucune réalisation dans cette catégorie.</p>}
        </div>
      </section>

      {/* CLIENTS */}
      <section id="clients" className="relative py-24 border-t border-white/5 bg-zinc-950/50">
        <div className="max-w-7xl mx-auto px-6 lg:px-8 mb-12">
          <p className="text-sm uppercase tracking-[0.3em] text-yellow-400 font-semibold mb-3">Ils me font confiance</p>
          <h2 className="font-display text-4xl font-black tracking-tighter">Mes clients</h2>
        </div>
        <div className="logo-marquee space-y-10 overflow-hidden">
          {[0, 1].map((rowIdx) => {
            const row = logos.filter((_, i) => i % 2 === rowIdx);
            if (row.length === 0) return null;
            const loop = [...row, ...row, ...row, ...row];
            return (
              <div key={rowIdx} className={`marquee-row ${rowIdx === 1 ? "reverse" : ""}`}>
                {loop.map((l, i) => (
                  <div
                    key={`${l.id}-${i}`}
                    className="mx-5 flex items-center justify-center bg-white rounded-2xl px-8 py-5 shadow-sm"
                  >
                    <img
                      data-testid="client-logo"
                      src={imgUrl(l.image_url)}
                      alt={l.name}
                      decoding="async"
                      className="h-12 md:h-16 object-contain"
                    />
                  </div>
                ))}
              </div>
            );
          })}
        </div>
      </section>

      {/* CONTACT */}
      <section id="contact" className="relative py-24 border-t border-white/5">
        <div className="max-w-3xl mx-auto px-6 lg:px-8 text-center">
          <p className="text-sm uppercase tracking-[0.3em] text-yellow-400 font-semibold mb-3">Contact</p>
          <h2 className="font-display text-4xl sm:text-5xl font-black tracking-tighter mb-4">Parlons de votre projet</h2>
          <p className="text-zinc-400 mb-10">Une idée, un devis, une question ? Écrivez-moi, je vous réponds rapidement.</p>
          <div className="bg-zinc-900/60 backdrop-blur-xl border border-white/10 rounded-2xl p-8 text-left">
            <LeadForm withMessage />
          </div>
        </div>
      </section>
      </main>

      <Footer settings={settings} />

      {/* LIGHTBOX */}
      <AnimatePresence>
        {lightbox && (
          <motion.div
            initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }}
            onClick={() => setLightbox(null)}
            data-testid="lightbox"
            className="fixed inset-0 z-[60] bg-black/90 backdrop-blur-sm flex items-center justify-center p-6"
          >
            <motion.img
              initial={{ scale: 0.9 }} animate={{ scale: 1 }}
              src={imgUrl(lightbox.image_url)} alt={lightbox.alt_text}
              className="max-h-[85vh] max-w-full rounded-lg object-contain"
            />
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  );
}

function FilterBtn({ label, isActive, onClick, testid }) {
  return (
    <button
      data-testid={testid}
      onClick={onClick}
      className={`px-5 py-2 rounded-full text-sm font-medium transition-colors ${
        isActive ? "bg-yellow-400 text-black" : "bg-white/5 text-zinc-300 hover:bg-white/10 border border-white/10"
      }`}
    >
      {label}
    </button>
  );
}

function CountUp({ to, suffix = "%" }) {
  const ref = useRef(null);
  const inView = useInView(ref, { once: true });
  const [val, setVal] = useState(0);
  useEffect(() => {
    if (!inView) return;
    const controls = animate(0, to, {
      duration: 1.2,
      ease: "easeOut",
      onUpdate: (v) => setVal(Math.round(v)),
    });
    return () => controls.stop();
  }, [inView, to]);
  return <span ref={ref}>{val}{suffix}</span>;
}

function SkillBar({ sk, i }) {
  const R = 46;
  const C = 2 * Math.PI * R;
  const ref = useRef(null);
  const inView = useInView(ref, { once: true });
  return (
    <motion.div
      ref={ref}
      initial={{ opacity: 0, y: 24 }}
      whileInView={{ opacity: 1, y: 0 }}
      viewport={{ once: true }}
      transition={{ duration: 0.5, delay: i * 0.12 }}
      className="group relative rounded-2xl border border-white/10 bg-zinc-900/40 p-6 flex flex-col items-center text-center hover:border-yellow-400/50 hover:bg-zinc-900/70 transition-colors"
    >
      <div className="relative w-32 h-32">
        <svg className="w-32 h-32 -rotate-90" viewBox="0 0 110 110">
          <circle cx="55" cy="55" r={R} fill="none" stroke="#27272A" strokeWidth="7" />
          <motion.circle
            cx="55" cy="55" r={R} fill="none" stroke="#FFE240" strokeWidth="7" strokeLinecap="round"
            strokeDasharray={C}
            initial={{ strokeDashoffset: C }}
            animate={inView ? { strokeDashoffset: C * (1 - sk.level / 100) } : {}}
            transition={{ duration: 1.4, delay: i * 0.12, ease: "easeOut" }}
          />
        </svg>
        <div className="absolute inset-0 flex flex-col items-center justify-center">
          {sk.icon && (
            <motion.img
              src={imgUrl(sk.icon)}
              alt={sk.name}
              className="w-9 h-9 object-contain mb-1"
              animate={{ y: [0, -5, 0] }}
              transition={{ duration: 3 + i * 0.4, repeat: Infinity, ease: "easeInOut", delay: i * 0.2 }}
            />
          )}
          <span className="font-display text-yellow-400 text-lg">
            {inView ? <CountUp to={sk.level} /> : "0%"}
          </span>
        </div>
      </div>
      <div className="mt-4 font-bold">{sk.name}</div>
      <div className="text-xs text-zinc-500 leading-snug mt-1">{sk.desc}</div>
    </motion.div>
  );
}

function RadialSkill(props) {
  return <SkillBar {...props} />;
}
